import React, { useCallback } from 'react'
import useFlow from 'modules/flow/state/use-flow'
import useOrder from 'modules/order/state/order/use-order'
import useStatus from 'modules/status/state/use-status'
import { StatusType } from 'modules/status/state/status-types'
import {
  trackFormSubmitEvent,
  trackFormErrorEvent,
} from 'modules/analytics/helpers/track-form-events'
import { PaymentSelectionButtonProps } from '../payment-selection-button'
import { StripeInlineCreditCardInputsRef } from './stripe-card-inputs'
import PaymentActionButton from '../payment-action-button'

import Bugsnag, { NotifiableError } from '@bugsnag/js'
import { getClassySessionId } from 'modules/utils/getClassySessionId'
import useFinalizeSingleStepOrder from '../../../../../order/finalize-order/use-finalize-single-step-order'

import { StructuredError } from 'types/error'
import { getCookieValue } from 'modules/utils/getCookieValue'
import {
  saveFormsData,
  validateForms,
} from 'modules/flow/components/steps/single/utils/single-step-form-orchestrator'

// Global refs to store form references
let globalCreditCardFormRef: React.RefObject<StripeInlineCreditCardInputsRef> | null = null

export const setCreditCardFormRef = (ref: React.RefObject<StripeInlineCreditCardInputsRef>) => {
  globalCreditCardFormRef = ref
}

const CreditCardActionButton = (props: PaymentSelectionButtonProps) => {
  const flow = useFlow()
  const order = useOrder()
  const status = useStatus()

  const finalizeSingleStepOrder = useFinalizeSingleStepOrder()

  const onClick = useCallback(async () => {
    trackFormSubmitEvent(flow.currentStep.name, { payment_type: order.paymentDetails.type })

    try {
      status.set(StatusType.SUBMITTING)

      if (!globalCreditCardFormRef?.current) {
        status.set(StatusType.FAILED, 'Credit card form is not available')
        return
      }

      // Step 1: Validate all visible forms
      const valitdatedForms = await validateForms()

      if (!valitdatedForms.success) {
        trackFormErrorEvent(
          flow.currentStep.name,
          valitdatedForms.error || 'Form validation failed',
        )

        // Don't show toast for form validation errors - errors are shown inline
        status.set(StatusType.IDLE)
      }

      // Validate all visible forms
      const validatedForms = await validateForms()

      if (!validatedForms.success) {
        trackFormErrorEvent(flow.currentStep.name, validatedForms.error || 'Form validation failed')

        status.set(
          StatusType.FAILED,
          validatedForms.error || 'Please correct the errors in the form',
        )
      }

      const creditCardError = await globalCreditCardFormRef.current.savePaymentMethodToStore()

      if (creditCardError) {
        trackFormErrorEvent(flow.currentStep.name, 'Credit card validation failed')

        // Don't show toast for credit card validation errors - errors are shown inline
        status.set(StatusType.IDLE)
      }

      if (!validatedForms.success || creditCardError) {
        return
      }

      // Save all form data using unified pattern
      saveFormsData(validatedForms.results || [], order)

      // Track successful payment method selection
      if (window.newrelic) {
        window.newrelic.addPageAction('payment_method_selected', {
          pgdid: getCookieValue('pgdid'),
          'classy-session-id': getClassySessionId(),
          paymentMethod: order.paymentDetails.type,
        })
      }

      await finalizeSingleStepOrder()
    } catch (e) {
      console.error(e)
      Bugsnag.notify(e as NotifiableError)

      // Now much simpler - StructuredError extends Error, so we can handle it uniformly
      const errorMessage =
        e instanceof StructuredError
          ? e // Pass the StructuredError directly
          : (e instanceof Error ? e.message : String(e)) ||
            'An error has occurred while trying to validate your payment method.'

      status.set(StatusType.FAILED, errorMessage)

      document
        .getElementById('payment-element')
        ?.scrollIntoView({ behavior: 'smooth', block: 'end', inline: 'nearest' })
    }
  }, [flow.currentStep.name, order, status, finalizeSingleStepOrder])

  return (
    <PaymentActionButton onClick={onClick} {...props}>
      {props.paymentMethodConfig.actionButtonText}
    </PaymentActionButton>
  )
}

export default CreditCardActionButton
